import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { RootState } from '../../../store/store';
import EmptyState from '../../../commodity/components/ui/EmptyState';
import ErrorState from '../../../commodity/components/ui/ErrorState';
import LoadingState from '../../../commodity/components/ui/LoadingState';
import { EnrichedEnrollment, getMyEnrollments } from '../api/enrollments';
import { getLearningProgress, ProgressSnapshot } from '../api/learning';
import type { DisplayTimeZone } from '../api/learner';
import LearnerFilter, {
  ALL_LEARNERS,
  LearnerOption,
  useHouseholdFilter,
} from '../components/LearnerFilter';
import { formatDeadline } from '../components/enrollmentRequest';
import { useHouseholdTime } from '../time/useFamilyTime';
import { familyZoneLabel, formatShortDateTime } from '../time/zonedTime';
import {
  ENROLLMENT_STATE_LABEL,
  isActiveLearning,
  NOT_ENOUGH_HISTORY_COPY,
  progressHeadline,
} from '../vocabulary/learningVocabulary';

/** The snapshots of every learner, or null for a learner whose snapshot could not be read. */
type SnapshotMap = Record<string, ProgressSnapshot>;

const learnerLabel = (e: EnrichedEnrollment) => e.childName ?? 'Alumno';

const countLabel = (n: number) => (n === 1 ? '1 clase activa' : `${n} clases activas`);

/**
 * UC-P08 "Mi aprendizaje" — the household's active learning (PRT-09-FR-01..03,
 * FR-09).
 *
 * A projection, not a second source: the enrollments are the server's, the
 * next session and the progress line are the server's progress snapshot
 * (UC-P09), and the words are the shared vocabulary. "Active" is one list
 * (ACTIVE_LEARNING_STATES), and the count is the length of the very list the
 * cards are drawn from, so the two cannot disagree. No money appears here.
 */
const ParentLearning: React.FC = () => {
  const [enrollments, setEnrollments] = useState<EnrichedEnrollment[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [snapshots, setSnapshots] = useState<SnapshotMap>({});
  /** Learners whose snapshot could not be read: their cards say so instead of guessing. */
  const [progressUnknown, setProgressUnknown] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useHouseholdFilter();
  const activeContext = useSelector((s: RootState) => s.context.activeContext);
  const { tz } = useHouseholdTime();

  const load = useCallback(async () => {
    setError(null);
    setEnrollments(null);
    try {
      const result = await getMyEnrollments();
      const list = result?.enrollments ?? [];
      setEnrollments(list);

      // Progress per learner, from the server's snapshot. A failure on one
      // learner costs that learner's progress line, never the list.
      const learners = Array.from(
        new Set(list.filter((e) => isActiveLearning(e.state)).map((e) => e.childId)),
      );
      const perLearner = await Promise.all(
        learners.map(async (id) => {
          try {
            const res = await getLearningProgress(id);
            return { id, snapshots: res?.snapshots ?? [] };
          } catch {
            return { id, snapshots: null };
          }
        }),
      );
      const map: SnapshotMap = {};
      const unknown = new Set<string>();
      for (const r of perLearner) {
        if (r.snapshots === null) unknown.add(r.id);
        for (const s of r.snapshots ?? []) map[s.enrollmentId] = s;
      }
      setSnapshots(map);
      setProgressUnknown(unknown);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const active = useMemo(
    () => (enrollments ?? []).filter((e) => isActiveLearning(e.state)),
    [enrollments],
  );

  const learners: LearnerOption[] = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of active) if (!seen.has(e.childId)) seen.set(e.childId, learnerLabel(e));
    // The learner the parent is acting as stays choosable even with nothing active.
    if (activeContext && !seen.has(activeContext.studentId)) {
      seen.set(activeContext.studentId, activeContext.studentName);
    }
    return Array.from(seen, ([studentId, name]) => ({ studentId, name }));
  }, [active, activeContext]);

  // One filtered list: the count, the groups and the empty state all read it.
  const shown = useMemo(() => {
    const list = filter === ALL_LEARNERS ? active : active.filter((e) => e.childId === filter);
    const next = (e: EnrichedEnrollment) =>
      snapshots[e.enrollmentId]?.nextSession?.scheduledStart ?? '9999';
    return [...list].sort(
      (a, b) =>
        next(a).localeCompare(next(b)) || (a.courseTitle ?? '').localeCompare(b.courseTitle ?? ''),
    );
  }, [active, filter, snapshots]);

  if (error) {
    return (
      <Frame>
        <ErrorState action="cargar tus clases" error={error} onRetry={load} />
      </Frame>
    );
  }
  if (enrollments === null) {
    return (
      <Frame>
        <LoadingState what="tus clases" />
      </Frame>
    );
  }

  const groups: Array<{ studentId: string; name: string; items: EnrichedEnrollment[] }> =
    filter === ALL_LEARNERS
      ? learners.map((l) => ({
          ...l,
          items: shown.filter((e) => e.childId === l.studentId),
        }))
      : [{ studentId: filter, name: '', items: shown }];

  const filteredName =
    filter === ALL_LEARNERS ? null : learners.find((l) => l.studentId === filter)?.name;
  const hasInactive = enrollments.length > active.length;

  return (
    <Frame>
      <LearnerFilter learners={learners} value={filter} onChange={setFilter} />

      <p aria-live="polite" style={{ fontWeight: 600, margin: '8px 0' }}>
        {countLabel(shown.length)}
        {filteredName ? ` de ${filteredName}` : ''}
      </p>

      {shown.length === 0 ? (
        <EmptyState
          message={
            filteredName
              ? `${filteredName} no tiene clases activas.`
              : filter === ALL_LEARNERS
              ? 'Todavía no hay clases activas.'
              : 'Este alumno no tiene clases activas.'
          }
          detail={
            hasInactive
              ? 'Las clases terminadas, canceladas o sin confirmar no aparecen aquí.'
              : 'Cuando inscribas a tu alumno en un curso, aparecerá aquí.'
          }
          action={{ label: 'Explorar cursos', to: '/parent/explore' }}
        />
      ) : (
        <>
          <p style={{ color: '#666', marginTop: 0, fontSize: 13 }}>{familyZoneLabel(tz)}</p>
          {groups
            .filter((g) => g.items.length > 0)
            .map((g) => (
              <section
                key={g.studentId}
                aria-label={g.name ? `Clases de ${g.name}` : undefined}
                style={{ marginBottom: 20 }}
              >
                {g.name && (
                  <h2 style={{ fontSize: 17, margin: '16px 0 8px' }}>
                    {g.name} · {countLabel(g.items.length)}
                  </h2>
                )}
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 12 }}>
                  {g.items.map((e) => (
                    <EnrollmentCard
                      key={e.enrollmentId}
                      enrollment={e}
                      snapshot={snapshots[e.enrollmentId]}
                      progressUnknown={progressUnknown.has(e.childId)}
                      tz={tz}
                    />
                  ))}
                </ul>
              </section>
            ))}
        </>
      )}
    </Frame>
  );
};

const Frame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ maxWidth: 760, margin: '0 auto' }}>
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <h1 style={{ margin: 0, flex: '1 1 auto' }}>Mi aprendizaje</h1>
      <Link
        to="/parent/schedule"
        className="btn btn-outline btn-sm"
        style={{ textDecoration: 'none' }}
      >
        Ver agenda
      </Link>
    </div>
    <p style={{ color: '#666', marginTop: 4 }}>
      Las clases en curso de tu familia, con su próxima sesión y su avance.
    </p>
    {children}
  </div>
);

/** One active enrollment: course, learner, teacher, state, next session, progress. */
const EnrollmentCard: React.FC<{
  enrollment: EnrichedEnrollment;
  snapshot?: ProgressSnapshot;
  progressUnknown: boolean;
  tz: DisplayTimeZone;
}> = ({ enrollment: e, snapshot, progressUnknown, tz }) => {
  const titleId = `learning-${e.enrollmentId}`;
  const waiting = e.state === 'PendingTeacherAcceptance';
  const headline = snapshot ? progressHeadline(snapshot) : undefined;

  return (
    <li
      aria-labelledby={titleId}
      style={{ border: '1px solid #d1d5da', borderRadius: 8, padding: 16, background: '#fff' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <strong id={titleId} style={{ fontSize: 16 }}>
            {e.courseTitle ?? 'Curso'}
          </strong>
          <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
            Alumno: {learnerLabel(e)}
            {e.teacherName && <> · Profesor: {e.teacherName}</>}
          </div>
        </div>
        <span
          style={{
            alignSelf: 'flex-start',
            fontSize: 12,
            fontWeight: 700,
            borderRadius: 4,
            padding: '3px 8px',
            background: waiting ? '#e7f5ff' : '#edf8e5',
            color: waiting ? '#1c4f80' : '#3f7410',
          }}
        >
          {ENROLLMENT_STATE_LABEL[e.state]}
        </span>
      </div>

      {waiting ? (
        <p style={{ fontSize: 14, color: '#1c4f80', margin: '10px 0 0' }}>
          Las clases empiezan cuando el profesor acepte. Tiene hasta el{' '}
          <strong>{formatDeadline(e.acceptBy, tz)}</strong> para responder.
        </p>
      ) : (
        <>
          <p style={{ fontSize: 14, margin: '10px 0 0' }}>
            Próxima sesión:{' '}
            <strong>
              {snapshot?.nextSession
                ? formatShortDateTime(snapshot.nextSession.scheduledStart, tz)
                : snapshot && !snapshot.degraded
                ? 'Sin sesiones próximas'
                : 'No disponible por ahora'}
            </strong>
          </p>
          <p style={{ fontSize: 14, margin: '4px 0 0' }} data-testid={`progress-${e.enrollmentId}`}>
            {headline
              ? `Avance: ${headline}`
              : headline === null
              ? NOT_ENOUGH_HISTORY_COPY
              : progressUnknown
              ? 'No pudimos cargar el avance. Vuelve a intentarlo más tarde.'
              : 'Avance: pendiente.'}
          </p>
        </>
      )}

      <div style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Link to={`/courses/${encodeURIComponent(e.courseId)}`}>Ver curso</Link>
        <Link to={`/enrollments/${encodeURIComponent(e.enrollmentId)}`}>Ver inscripción</Link>
      </div>
    </li>
  );
};

export default ParentLearning;
